import { Input, Tree } from "@renderbus/ui";
import { Empty } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { TransferPropsType, IdeptInfo } from "../../types";
import {
  ProjectTransferContainer,
  ProjectTransferItem,
  ProjectTransferHeader,
  ProjectTransferRightItem,
  ProjectTransferBody,
  ProjectTransferAction,
  ProjectTransferWrapper,
  NotFoundWrapper,
} from "./atom";
import { RightOutlined, DeleteOutlined } from "@ant-design/icons";
import { makeUpSubmitArr } from "../../helper";
import { useCallback, useEffect, useState } from "react";
import { PatternMatch } from "project-config/utils/pattern-match";
import { searchDingtalkTree } from "../../helper/search-dingtalk-tree";
import NotFoundIcon from "@/assets/icons/project-manage/not-found";

export function ProjectTransfer({
  transferProps,
  isDingTalkOriganize,
  dingTalkDataSource = [],
}: {
  transferProps: TransferPropsType;
  isDingTalkOriganize?: boolean;
  dingTalkDataSource?: IdeptInfo[];
}) {
  const { t } = useTranslation("center");
  const { t: childManagerT } = useTranslation("childManager");
  const { dataSource, targetKeys, onSelectChange, handleRemoveChange } =
    transferProps;
  const isEmptyContent = useCallback(() => {
    return isDingTalkOriganize
      ? !dingTalkDataSource.length
      : !dataSource.length;
  }, [dataSource.length, dingTalkDataSource, isDingTalkOriganize]);
  const [_dataSource, setDataSource] = useState([...dataSource]);
  const [_dingTalkDataSource, setDingTalkDataSource] = useState([
    ...dingTalkDataSource,
  ]);
  const [_isEmptyContent, setIsEmptyContent] = useState(isEmptyContent);
  const [searchKey, setSearchKey] = useState("");
  const handleSearch = useCallback(
    (value: string) => {
      setSearchKey(value);
      if (!value) {
        setDataSource([...dataSource]);
        setDingTalkDataSource([...dingTalkDataSource]);
        setIsEmptyContent(isEmptyContent());
        return;
      }
      if (!isDingTalkOriganize) {
        const searchDataSource = dataSource.filter((item) =>
          PatternMatch.fuzzyMatch(item.title as string, value)
        );
        setDataSource(searchDataSource);
        setIsEmptyContent(!searchDataSource.length);
      } else {
        const searchDataSource = searchDingtalkTree(dingTalkDataSource, value);
        setDingTalkDataSource(searchDataSource);
        setIsEmptyContent(!searchDataSource.length);
      }
    },
    [dataSource, dingTalkDataSource, isDingTalkOriganize, isEmptyContent]
  );
  useEffect(() => {
    setIsEmptyContent(isEmptyContent());
    setDataSource([...dataSource]);
    setDingTalkDataSource([...dingTalkDataSource]);
    if (searchKey) {
      handleSearch(searchKey);
    }
  }, [
    dataSource,
    dingTalkDataSource,
    handleSearch,
    isDingTalkOriganize,
    isEmptyContent,
    searchKey,
  ]);
  useEffect(() => {
    setSearchKey("");
  }, [isDingTalkOriganize]);

  return (
    <ProjectTransferWrapper>
      <ProjectTransferContainer>
        <ProjectTransferHeader>
          {t("user_com_transfer_optionText")}
        </ProjectTransferHeader>
        <ProjectTransferBody>
          <Input
            suffix={!searchKey && <SearchOutlined />}
            value={searchKey}
            className="search-input"
            allowClear
            onChange={(e) => {
              handleSearch(e.target.value);
            }}
          />
          {_isEmptyContent ? (
            searchKey ? (
              <NotFoundWrapper>
                <NotFoundIcon />
                <span>{t("project_member_search_not_found")}</span>
              </NotFoundWrapper>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          ) : (
            <>
              {!isDingTalkOriganize ? (
                _dataSource.map((item) => {
                  return (
                    <ProjectTransferItem
                      key={item.description}
                      onClick={() =>
                        onSelectChange([
                          {
                            text: item.title ?? "",
                            id: item.description ?? "",
                            deptIdList: null,
                            isDingTalkOriganize: !!isDingTalkOriganize,
                          },
                        ])
                      }
                    >
                      {item.title}
                    </ProjectTransferItem>
                  );
                })
              ) : (
                <Tree
                  className={"dingtalk-init-tree"}
                  treeData={makeUpSubmitArr(_dingTalkDataSource)}
                  defaultExpandAll={!!searchKey}
                  fieldNames={{
                    title: "name",
                    key: "keyIndex",
                    children: "deptList",
                  }}
                  defaultExpandedKeys={[_dingTalkDataSource[0]?.keyIndex]}
                  titleRender={(data: any) => {
                    return (
                      <div
                        className="child-manage-tree-container"
                        onClick={() =>
                          onSelectChange([
                            {
                              text: data.name,
                              id: data.id,
                              deptId: data.realDeptId,
                              deptIdList: [data.realDeptId],
                              isDingTalkOriganize: !!isDingTalkOriganize,
                            },
                          ])
                        }
                      >
                        <div>{data.name}</div>
                        {data.isLeader === 1 && (
                          <div className="child-manage-leader">
                            {childManagerT("dingtalk_subaccount_director")}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
              )}
            </>
          )}
        </ProjectTransferBody>
      </ProjectTransferContainer>
      <ProjectTransferAction>
        <RightOutlined />
      </ProjectTransferAction>
      <ProjectTransferContainer>
        <ProjectTransferHeader>
          {t("user_com_transfer_selectText")}
        </ProjectTransferHeader>
        <ProjectTransferBody>
          {targetKeys.length ? (
            targetKeys.map((item) => {
              return (
                <ProjectTransferRightItem key={item.id}>
                  <span>{item.text}</span>
                  <DeleteOutlined
                    onClick={() =>
                      handleRemoveChange({
                        text: item.text,
                        id: item.id,
                        deptIdList: item.deptIdList,
                        // @ts-ignore
                        deptId:
                          item.deptIdList === null
                            ? [item.deptId]
                            : item.deptIdList,
                        isDingTalkOriganize: item.isDingTalkOriganize,
                      })
                    }
                  />
                </ProjectTransferRightItem>
              );
            })
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </ProjectTransferBody>
      </ProjectTransferContainer>
    </ProjectTransferWrapper>
  );
}
